import { Box, Checkbox, Chip, TextField, makeStyles } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import React, { useEffect, useState } from 'react';
import toastError from '../../errors/toastError';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';

/** Cores estáveis por usuário (hash do id), leitura boa em fundo claro. */
const CHIP_PALETTE = [
  { bg: '#e3f2fd', color: '#0d47a1' },
  { bg: '#f3e5f5', color: '#4a148c' },
  { bg: '#e8f5e9', color: '#1b5e20' },
  { bg: '#fff3e0', color: '#e65100' },
  { bg: '#fce4ec', color: '#880e4f' },
  { bg: '#e0f7fa', color: '#006064' },
  { bg: '#f1f8e9', color: '#33691e' },
  { bg: '#ede7f6', color: '#311b92' },
  { bg: '#fff8e1', color: '#ff6f00' },
  { bg: '#e8eaf6', color: '#1a237e' },
  { bg: '#ffebee', color: '#b71c1c' },
  { bg: '#e0f2f1', color: '#004d40' },
  { bg: '#e1f5fe', color: '#01579b' },
  { bg: '#f9fbe7', color: '#827717' },
];

function chipColorsForUserId(userId) {
  const s = String(userId ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return CHIP_PALETTE[Math.abs(h) % CHIP_PALETTE.length];
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    paddingBottom: theme.spacing(1.25),
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      flexWrap: 'wrap',
      alignItems: 'center',
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
    '& .MuiAutocomplete-tag': {
      margin: theme.spacing(0.5, 0.75, 0.5, 0),
    },
  },
  chip: {
    height: 28,
    borderRadius: 8,
    fontWeight: 500,
    fontSize: '0.8125rem',
    border: 'none',
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
      opacity: 0.55,
      '&:hover': {
        opacity: 1,
        color: theme.palette.error.main,
      },
    },
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
  },
  optionCheckbox: {
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  optionLabel: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

export function UsersFilter({ onFiltered, initialUsers }) {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [selecteds, setSelecteds] = useState([]);

  useEffect(() => {
    async function fetchData() {
      await loadUsers();
    }
    fetchData();
  }, []);

  useEffect(() => {
    setSelecteds([]);
    if (Array.isArray(initialUsers) && Array.isArray(users) && users.length > 0) {
      onChange(initialUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUsers, users]);

  const loadUsers = async () => {
    try {
      const { data } = await api.get(`/users/list`);
      const userList = data.map((u) => ({ id: u.id, name: u.name }));
      setUsers(userList);
    } catch (err) {
      toastError(err);
    }
  };

  const onChange = async (value) => {
    setSelecteds(value);
    onFiltered(value);
  };

  return (
    <Box className={classes.root}>
      <Autocomplete
        multiple
        className={classes.autocomplete}
        size="small"
        options={users}
        value={selecteds}
        onChange={(e, v) => onChange(v)}
        disableCloseOnSelect
        getOptionLabel={(option) => option?.name || ''}
        getOptionSelected={(option, value) =>
          option?.id === value?.id ||
          (option?.name && value?.name && option.name.toLowerCase() === value.name.toLowerCase())
        }
        renderOption={(option, { selected }) => (
          <div className={classes.optionRow}>
            <Checkbox
              className={classes.optionCheckbox}
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              checked={selected}
              color="primary"
            />
            <span className={classes.optionLabel}>{option.name}</span>
          </div>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { bg, color } = chipColorsForUserId(option?.id);
            return (
              <Chip
                key={option?.id ?? index}
                {...getTagProps({ index })}
                label={option.name}
                size="small"
                className={classes.chip}
                style={{ backgroundColor: bg, color }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={i18n.t('tickets.search.filterUsers')}
          />
        )}
        ListboxProps={{
          style: { maxHeight: 280 },
        }}
      />
    </Box>
  );
}
